import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Download, Loader2, Inbox, FileCheck2, Trophy, Percent, Timer, Coins, ChevronLeft, ChevronRight } from "lucide-react";

const NAVY = "#1B4383", GOLD = "#A87F31", GREEN = "#2F6B45", RED = "#B4413C";
const compact = (v) => (v >= 1e9 ? `${+(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : `${v}`);
const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(+y, +mo - 1, 1).toLocaleString("en-GB", { month: "short", year: "2-digit" }); };

const shiftMonth = (m, k) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + k, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const NOW_MONTH = new Date().toISOString().slice(0, 7);

function MonthNav({ anchor, setAnchor, testid }) {
  const btn = "inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-slate-600 hover:bg-slate-50 disabled:opacity-30";
  return (
    <div className="flex shrink-0 items-center gap-1" data-testid={testid}>
      <button type="button" className={btn} onClick={() => setAnchor(shiftMonth(anchor, -1))} aria-label="Previous Month" data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
      <span className="min-w-[118px] text-center text-[11px] font-semibold text-slate-600">{monthLabel(shiftMonth(anchor, -11))} – {monthLabel(anchor)}</span>
      <button type="button" className={btn} onClick={() => setAnchor(shiftMonth(anchor, 1))} disabled={anchor >= NOW_MONTH} aria-label="Next Month" data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-[11px] font-semibold tracking-wide text-slate-500">{label}</span><Icon className="h-4 w-4 text-[#1B4383]" /></div>
      <div className="mt-2 font-heading text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Card({ title, sub, children, testid, action }) {
  return (
    <div className="min-w-0 rounded-xl border border-border bg-white p-5" data-testid={testid}>
      <div className="mb-4 flex items-start justify-between gap-2"><div><h3 className="font-heading text-sm font-bold text-slate-800">{title}</h3>{sub && <p className="text-xs text-muted-foreground">{sub}</p>}</div>{action}</div>
      {children}
    </div>
  );
}

function Table({ rows, first, ccy, fx, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-[11px] text-slate-500">
          <th className="py-2 pr-3 font-semibold">{first}</th><th className="py-2 pr-3 text-right font-semibold">RFQs</th><th className="py-2 pr-3 text-right font-semibold">Quoted</th><th className="py-2 pr-3 text-right font-semibold">Won</th><th className="py-2 pr-3 text-right font-semibold">Win Rate</th><th className="py-2 text-right font-semibold">Quoted Value</th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.name} className="border-b border-border/60 last:border-0">
            <td className="max-w-[220px] truncate py-2 pr-3 font-medium text-slate-800">{r.name}</td>
            <td className="py-2 pr-3 text-right">{r.received}</td><td className="py-2 pr-3 text-right">{r.quoted}</td><td className="py-2 pr-3 text-right">{r.won}</td>
            <td className="py-2 pr-3 text-right">{r.win_rate}%</td><td className="py-2 text-right font-mono text-xs">{ccy} {compact(fx(r.quoted_value))}</td>
          </tr>
        ))}</tbody>
      </table>
      {!rows.length && <p className="py-6 text-center text-sm text-muted-foreground">No RFQs In This Period</p>}
    </div>
  );
}

export default function RFQReports() {
  const [period, setPeriod] = useState("year");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ccy, setCcy] = useState("USD");
  const [d, setD] = useState(null);
  const [allMonthly, setAllMonthly] = useState({});
  const [anchor, setAnchor] = useState(NOW_MONTH);
  useEffect(() => {
    api.get("/rfq-reports?period=all").then((r) => setAllMonthly(Object.fromEntries(r.data.monthly.map((m) => [m.name, m])))).catch(() => {});
  }, []);
  useEffect(() => { setAnchor(period === "month" ? month : NOW_MONTH); }, [period, month]);
  const [busy, setBusy] = useState(false);
  const p = period === "month" ? month : period;
  useEffect(() => {
    setD(null);
    api.get(`/rfq-reports?period=${p}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [p]);
  const fx = (v) => (ccy === "USD" && d?.usd_rate ? v / d.usd_rate : v);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/rfq-reports/export?period=${p}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Somcore-RFQ-Report-${p}.xlsx`; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const tab = (k, l) => <button key={k} onClick={() => setPeriod(k)} className={`px-3 py-1.5 text-xs font-semibold ${period === k ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`report-period-${k}`}>{l}</button>;
  const s = d?.summary;
  const empty = { received: 0, quoted: 0, won: 0, quoted_value: 0, won_value: 0, win_rate: 0 };
  const monthly = Array.from({ length: 12 }, (_, i) => shiftMonth(anchor, i - 11)).map((name) => {
    const m = allMonthly[name] || empty;
    return { ...m, name, label: monthLabel(name), qv: fx(m.quoted_value), wv: fx(m.won_value) };
  });
  const nav = (t) => <MonthNav anchor={anchor} setAnchor={setAnchor} testid={t} />;

  return (
    <div className="space-y-5" data-testid="rfq-reports">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-border">{tab("month", "Month")}{tab("year", "This Year")}{tab("all", "All Time")}</div>
          {period === "month" && <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-8 w-40" data-testid="report-month-input" />}
          <div className="inline-flex overflow-hidden rounded-md border border-border">{["USD", "TZS"].map((c) => <button key={c} onClick={() => setCcy(c)} className={`px-3 py-1.5 text-xs font-semibold ${ccy === c ? "bg-[#12294B] text-white" : "bg-white text-slate-600"}`} data-testid={`report-ccy-${c}`}>{c}</button>)}</div>
        </div>
        <Button variant="outline" onClick={download} disabled={busy || !d} data-testid="report-export-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Download Excel</Button>
      </div>

      {!d ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (<>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Stat icon={Inbox} label="RFQs Received" value={s.received} testid="report-stat-received" />
          <Stat icon={FileCheck2} label="Quoted" value={s.quoted} sub={s.received ? `${Math.round((100 * s.quoted) / s.received)}% Of Received` : ""} testid="report-stat-quoted" />
          <Stat icon={Trophy} label="Won" value={s.won} sub={`${s.lost} Lost`} testid="report-stat-won" />
          <Stat icon={Percent} label="Win Rate" value={`${s.win_rate}%`} sub="Won ÷ Quoted" testid="report-stat-winrate" />
          <Stat icon={Coins} label="Quoted Value" value={`${ccy} ${compact(fx(s.quoted_value))}`} sub={`Won ${ccy} ${compact(fx(s.won_value))}`} testid="report-stat-value" />
          <Stat icon={Timer} label="Avg Days To Quote" value={s.avg_days_to_quote ?? "—"} sub="Received → Quoted" testid="report-stat-days" />
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="RFQs By Month" sub="Received vs Quoted vs Won" testid="report-chart-monthly" action={nav("report-monthly-nav")}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={32} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="received" name="Received" fill={NAVY} radius={[3, 3, 0, 0]} /><Bar dataKey="quoted" name="Quoted" fill={GOLD} radius={[3, 3, 0, 0]} /><Bar dataKey="won" name="Won" fill={GREEN} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card title="Quoted Value & Win Rate By Month" sub={`Values In ${ccy}`} testid="report-chart-value" action={nav("report-value-nav")}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthly}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis yAxisId="v" tickFormatter={compact} tick={{ fontSize: 11, fill: "#64748b" }} width={48} /><YAxis yAxisId="r" orientation="right" unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} width={40} />
                <Tooltip formatter={(v, n) => (n === "Win Rate" ? `${v}%` : `${ccy} ${compact(v)}`)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="v" type="monotone" dataKey="qv" name="Quoted Value" stroke={NAVY} strokeWidth={2} dot={{ r: 3 }} /><Line yAxisId="v" type="monotone" dataKey="wv" name="Won Value" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} /><Line yAxisId="r" type="monotone" dataKey="win_rate" name="Win Rate" stroke={RED} strokeDasharray="4 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="min-w-0 lg:col-span-2"><Card title="Top Clients By RFQs" sub="Top 10 In This Period" testid="report-clients"><Table rows={d.clients} first="Client" ccy={ccy} fx={fx} testid="report-clients-table" /></Card></div>
          <Card title="RFQs By Stage" testid="report-stages">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.stages.map((x) => ({ ...x, label: x.stage.charAt(0).toUpperCase() + x.stage.slice(1) }))} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} /><YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: "#334155" }} width={64} /><Tooltip /><Bar dataKey="count" name="RFQs" fill={NAVY} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {d.unquoted_reasons?.length > 0 && (
              <div className="mt-3 border-t border-border pt-3" data-testid="report-unquoted-reasons">
                <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-slate-500">Why Unquoted</div>
                {d.unquoted_reasons.map((x) => <div key={x.reason} className="flex justify-between text-sm"><span className="truncate text-slate-700">{x.reason}</span><span className="font-semibold">{x.count}</span></div>)}
              </div>
            )}
          </Card>
        </div>

        <Card title="By Owner" sub="Who Is Handling The RFQs" testid="report-owners"><Table rows={d.owners} first="Owner" ccy={ccy} fx={fx} testid="report-owners-table" /></Card>
      </>)}
    </div>
  );
}
